import { apiFetch } from "./client";

export type TemplateArt = "buchungsbestaetigung" | "infos_gruppe" | "rechnung" | "konus_link";

export type TemplateMeta = {
  art: TemplateArt;
  typ: string;
  label: string;
  is_user_override: boolean;
};

export type Template = TemplateMeta & {
  betreff: string;
  inhalt: string;
};

export async function listMyTemplates(): Promise<TemplateMeta[]> {
  return apiFetch<TemplateMeta[]>("/api/me/templates");
}

export async function getMyTemplate(art: TemplateArt): Promise<Template> {
  return apiFetch<Template>(`/api/me/templates/${art}`);
}

export async function putMyTemplate(art: TemplateArt, betreff: string, inhalt: string): Promise<void> {
  await apiFetch(`/api/me/templates/${art}`, {
    method: "PUT",
    body: { betreff, inhalt },
  });
}

export async function resetMyTemplate(art: TemplateArt): Promise<void> {
  await apiFetch(`/api/me/templates/${art}`, { method: "DELETE" });
}

export async function changeMyPassword(aktuell: string, neu: string): Promise<void> {
  await apiFetch("/api/me/password", { method: "POST", body: { aktuell, neu } });
}

export async function updateMyDisplayName(displayName: string): Promise<{ display_name: string }> {
  return apiFetch<{ display_name: string }>("/api/me/display_name", {
    method: "PUT",
    body: { display_name: displayName },
  });
}

export type SettingsConfig = {
  KURTAXE_AB_16: string;
  KURTAXE_6_BIS_15: string;
  KURTAXE_UNTER_6: string;
  ANZAHLUNG_BETRAG: string;
  STORNO_SCHWELLE_TAGE: string;
  STORNO_PCT: string;
  DASHBOARD_KONUS_LINK_FRIST_TAGE: string;
  DASHBOARD_KONUS_DATEN_FRIST_TAGE: string;
  DASHBOARD_INFOS_GRUPPE_FRIST_TAGE: string;
  DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE: string;
  DASHBOARD_ANZAHLUNG_FRIST_TAGE: string;
  FORMULARE_ANFRAGE_AKTIV: string;
  FORMULARE_KONUS_AKTIV: string;
  KONUS_HOTELID: string;
  KONUS_KATEGORIE_ERWACHSEN: string;
  KONUS_KATEGORIE_KIND: string;
  KONUS_KATEGORIE_KLEINKIND: string;
  CI_NAME: string;
  CI_PRIMAERFARBE: string;
  CI_AKZENTFARBE: string;
  CI_ADRESSE: string;
  CI_TELEFON: string;
  CI_EMAIL: string;
  CI_WEBSITE: string;
  CI_FONT_UEBERSCHRIFT: string;
  CI_FONT_TEXT: string;
};

/** Websichere Schriften, die in E-Mail-Clients (inkl. Outlook) zuverlässig funktionieren. */
export const WEBSICHERE_FONTS = [
  "Arial",
  "Georgia",
  "Times New Roman",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
] as const;

export async function getSettingsConfig(): Promise<SettingsConfig> {
  return apiFetch<SettingsConfig>("/api/settings/config");
}

export async function putSettingsConfig(patch: Partial<SettingsConfig>): Promise<SettingsConfig> {
  return apiFetch<SettingsConfig>("/api/settings/config", { method: "PUT", body: patch });
}

export type CiPreviewVariante = "buchungsbestaetigung" | "infos_gruppe" | "rechnung";

type CiPreviewBody = {
  variante: CiPreviewVariante;
} & Partial<
  Pick<
    SettingsConfig,
    | "CI_NAME"
    | "CI_PRIMAERFARBE"
    | "CI_AKZENTFARBE"
    | "CI_ADRESSE"
    | "CI_TELEFON"
    | "CI_EMAIL"
    | "CI_WEBSITE"
    | "CI_FONT_UEBERSCHRIFT"
    | "CI_FONT_TEXT"
  >
>;

/** Rendert die Beispielmail mit dem echten Backend-Template und liefert HTML zurück. */
export async function getCiDesignPreview(body: CiPreviewBody): Promise<string> {
  // Antwort ist text/html; apiFetch liefert für Nicht-JSON automatisch den Text.
  return apiFetch<string>("/api/settings/ci-design/preview", {
    method: "POST",
    body,
  });
}

export type PriceRow = {
  id?: number;
  einheit_id: number;
  gast_typ: string;
  saison: string;
  saison_von: string;
  saison_bis: string;
  preis_pro_nacht: number;
};

export type SettingsPrices = {
  einheiten: Array<{ id: number; name: string }>;
  gasttypen: string[];
  saisons: string[];
  rows: PriceRow[];
};

export async function getSettingsPrices(): Promise<SettingsPrices> {
  return apiFetch<SettingsPrices>("/api/settings/prices");
}

export async function putSettingsPrices(rows: PriceRow[]): Promise<SettingsPrices> {
  return apiFetch<SettingsPrices>("/api/settings/prices", {
    method: "PUT",
    body: { rows },
  });
}

export type AppUser = {
  id: number;
  username: string;
  display_name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export async function listUsers(): Promise<AppUser[]> {
  return apiFetch<AppUser[]>("/api/users");
}

export async function createUser(payload: {
  username: string;
  display_name?: string;
  password: string;
  role?: string;
  is_active?: boolean;
}): Promise<AppUser> {
  return apiFetch<AppUser>("/api/users", { method: "POST", body: payload });
}

export async function patchUser(
  id: number,
  patch: { display_name?: string; role?: string; is_active?: boolean },
): Promise<void> {
  await apiFetch(`/api/users/${id}`, { method: "PATCH", body: patch });
}

export async function setUserPassword(id: number, password: string): Promise<void> {
  await apiFetch(`/api/users/${id}/password`, { method: "POST", body: { password } });
}

export type MailVersandModus = "eml_download" | "smtp_preview";
export type MailTransport = "smtp" | "microsoft_graph";

export type MailConfig = {
  versand_modus: string;
  transport: string;
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_password: string;
  smtp_from: string;
  graph_tenant_id: string;
  graph_client_id: string;
  graph_client_secret: string;
  graph_mailbox: string;
  imap_host: string;
  imap_port: string;
  imap_user: string;
  imap_password: string;
  imap_use_ssl: string;
  posteingang_aktiv: string;
};

export async function getMailConfig(): Promise<MailConfig> {
  return apiFetch<MailConfig>("/api/me/mail-settings");
}

export async function putMailConfig(patch: Partial<MailConfig>): Promise<MailConfig> {
  return apiFetch<MailConfig>("/api/me/mail-settings", { method: "PUT", body: patch });
}

export async function testMailConfig(): Promise<{ ok: boolean; detail: string }> {
  return apiFetch<{ ok: boolean; detail: string }>("/api/me/mail-settings/test", {
    method: "POST",
  });
}

export async function testImapConfig(): Promise<{ ok: boolean; detail: string }> {
  return apiFetch<{ ok: boolean; detail: string }>("/api/me/mail-settings/test-imap", {
    method: "POST",
  });
}
